import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ReferralSourceMappings } from '@headless-shell/shared-components';
import { CONFIG } from '../../utils/config';
import { loadRemoteModule } from '../../utils/loadRemoteModule';

let ContactModule = null;
let mountedInstance = null;
let isCurrentlyMounted = false;

const CONTACT_US_ROOT_ID = 'contact-us-root';

function getContactUsContainer() {
  return document.querySelector(`#${CONTACT_US_ROOT_ID}`);
}

function cleanupContainer() {
  const container = getContactUsContainer();
  if (container) container.innerHTML = '';
}

async function loadContactModule() {
  if (ContactModule) return;
  try {
    const module = await loadRemoteModule({
      scope: 'embd_global_contact_ui',
      remoteBaseUrl: CONFIG.CONTACTUS_MFE_URL,
      exposedModule: './GlobalContactApp',
      initializeSharing: false,
    });
    if (module?.mount && module?.unmount) {
      ContactModule = module;
    } else if (module?.default) {
      ContactModule = module.default;
    } else {
      throw new Error('Contact module does not export mount/unmount');
    }
  } catch (error) {
    console.error('❌ Contact module loading failed:', error);
    throw error;
  }
}


export async function bootstrap(props) {
  return Promise.resolve();
}

export async function mount(options, handleClose) {
  await loadContactModule();

  if (isCurrentlyMounted) {
    console.warn('ContactUs already mounted, skipping');
    return;
  }

  const contactUsContainer = getContactUsContainer();
  if (!contactUsContainer) {
    console.error(`❌ ContactUs container (#${CONTACT_US_ROOT_ID}) not found in DOM`);
    throw new Error('ContactUs container not found');
  }

  cleanupContainer();

  try {
    const mountFn = ContactModule?.mount || ContactModule?.default?.mount;
    if (typeof mountFn !== 'function') {
      console.error('❌ No mount function found in Contact module');
      throw new Error('ContactUs mount function not found');
    }

    mountedInstance = await mountFn(
      contactUsContainer,
      {
        options,
        initialPath: window.location.pathname,
        onNavigate: ({ pathname: nextPathname }) => {
          handleClose && handleClose();
        },
      }
    );
    isCurrentlyMounted = true;
    console.log('✅ ContactUs mounted successfully');
  } catch (error) {
    console.error('❌ ContactUs mount error:', error);
    isCurrentlyMounted = false;
    mountedInstance = null;
    cleanupContainer();
    throw error;
  }
}

export async function unmount() {
  await loadContactModule();

  if (!isCurrentlyMounted) {
    console.log('ContactUs not currently mounted, skipping unmount');
    return;
  }

  try {
    const unmountFn = ContactModule?.unmount || ContactModule?.default?.unmount;
    if (typeof unmountFn === 'function') {
      await unmountFn(getContactUsContainer());
      console.log('✅ ContactUs module unmount completed');
    }
  } catch (error) {
    console.error('⚠️ ContactUs unmount error:', error);
  } finally {
    cleanupContainer();
    mountedInstance = null;
    isCurrentlyMounted = false;
    console.log('✅ ContactUs cleanup completed');
  }
}

export default function ContactUs({ applicationId, options: opts = {}, onClose: handleClose, applicationName, navigate }) {
  const { userInfo = {} } = useSelector((state) => state.userContext);
  const { adpWebID = '' } = userInfo || {};
  const { locale = '' } = useSelector(state => state.app)
  const currentEnv = process.env.REACT_APP_ENV_NAME || 'iat';

  const options = {
    language: 'en-US',
    targetElementId: CONTACT_US_ROOT_ID,
    authenticateUser: false,
    navigate: navigate || window.singleSpa?.navigateToUrl,
    preFillFormOptions: [],
    userInfo: adpWebID ? { adpWebID, ...userInfo.userInfo } : null,
    disableAuth :true,
    referringSource: `App store ${ReferralSourceMappings[locale] || 'US'} ${currentEnv.toUpperCase()}`,
    qs_partner: applicationId || null,
     ...(applicationId ? { type: 'referral' } : {}),
    appName: applicationName || null,
    ...opts
  };

  useEffect(() => {
    let isMounted = true;

    async function doMount() {
      try {
        await bootstrap(options);
        await mount(options, handleClose);
      } catch (err) {
        console.error('ContactUs mount failed:', err);
      }
    }

    doMount();

    return () => {
      if (isMounted) {
        unmount();
        isMounted = false;
      }
    };
  }, [JSON.stringify(options), handleClose]);

  return <div id={CONTACT_US_ROOT_ID} />;
}